import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { FaLock, FaShieldAlt, FaSearch, FaDatabase, FaHeadset } from 'react-icons/fa';
import '../styles/Managed.css';

const CyberSecurityServices = () => {
  useEffect(() => {
      window.scrollTo(0, 0); // Force scroll to top

    AOS.init({ duration: 800, once: true });
  }, []);

  return (
    <div className="desktop-support py-5">
      {/* Hero Section */}
      <Container className="mb-5" data-aos="fade-down">
        <Row className="align-items-center">
          <Col md={6} className="mb-4 mb-md-0">
            <h1 className="fw-bold hero-title">Cybersecurity Services</h1>
            <p className="text-muted hero-tagline">
              Our advanced <strong>cybersecurity solutions</strong> safeguard your business from modern digital threats.
              We combine <em>threat intelligence, AI-driven monitoring, and expert human response</em> to secure your
              networks, applications, and sensitive data. From <strong>firewall management</strong> to{" "}
              <strong>incident response</strong> and <strong>compliance auditing</strong>, we protect your organization
              against cyber attacks, ensuring business continuity and regulatory compliance.
            </p>
          </Col>
          <Col md={6} data-aos="fade-left">
            <video
              src="/videos/cyber_security.mov"
              className="img-fluid rounded shadow network-video mt-5"
              autoPlay
              loop
              muted
              playsInline
            />
          </Col>
        </Row>
      </Container>

      {/* Overview Section */}
      <Container className="mb-5" data-aos="fade-up">
        <h3 className="fw-bold text-center mb-3">Comprehensive Cybersecurity Coverage</h3>
        <p className="text-muted text-center mb-4 mx-auto info-text">
          We deliver proactive, end-to-end cybersecurity services to protect your IT environment against known and
          emerging threats. Our solutions cover every layer of your digital ecosystem — from perimeter defense to
          endpoint protection — ensuring maximum security and minimal downtime.
        </p>
        <Row className="justify-content-center">
          <Col md={8}>
            <ul className="list-unstyled support-list">
              <li data-aos="fade-right">Next-Generation Firewall Management</li>
              <li data-aos="fade-right" data-aos-delay="100">24/7 Threat Detection & Response</li>
              <li data-aos="fade-right" data-aos-delay="200">Advanced Malware & Ransomware Protection</li>
              <li data-aos="fade-right" data-aos-delay="300">Data Encryption & Secure Cloud Backup</li>
              <li data-aos="fade-right" data-aos-delay="400">Security Awareness Training for Employees</li>
            </ul>
          </Col>
        </Row>
      </Container>

      {/* Features Section */}
      <Container className="mb-5">
        <Row className="g-4">
          <Col md={4} data-aos="fade-up">
            <Card className="p-4 text-center h-100 shadow-sm glass-card">
              <FaShieldAlt size={40} className="text-primary mb-3" />
              <h5>Network Security</h5>
              <p className="text-muted">Multi-layered defense to secure your networks from unauthorized access.</p>
            </Card>
          </Col>
          <Col md={4} data-aos="fade-up" data-aos-delay="100">
            <Card className="p-4 text-center h-100 shadow-sm glass-card">
              <FaSearch size={40} className="text-success mb-3" />
              <h5>Threat Monitoring</h5>
              <p className="text-muted">24/7 monitoring to detect and neutralize threats in real time.</p>
            </Card>
          </Col>
          <Col md={4} data-aos="fade-up" data-aos-delay="200">
            <Card className="p-4 text-center h-100 shadow-sm glass-card">
              <FaLock size={40} className="text-danger mb-3" />
              <h5>Data Protection</h5>
              <p className="text-muted">Encryption and access control to protect your sensitive information.</p>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Additional Services */}
      <Container className="mb-5">
        <Row className="g-4">
          <Col md={6} data-aos="zoom-in">
            <Card className="p-4 text-center h-100 shadow-sm glass-card">
              <FaDatabase size={35} className="text-info mb-3" />
              <h6>Secure Backup & Recovery</h6>
              <p className="text-muted">Automated backups and fast recovery to ensure business continuity.</p>
            </Card>
          </Col>
          <Col md={6} data-aos="zoom-in" data-aos-delay="100">
            <Card className="p-4 text-center h-100 shadow-sm glass-card">
              <FaHeadset size={35} className="text-warning mb-3" />
              <h6>24/7 Security Support</h6>
              <p className="text-muted">Our experts are on standby to respond to any security incident.</p>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Why Choose Us */}
      <Container className="mb-5">
        <h3 className="text-center fw-bold mb-4" data-aos="fade-right">Why Choose Our Cybersecurity Services?</h3>
        <Row className="g-4">
          <Col md={6} lg={3} data-aos="fade-right">
            <Card className="text-center p-4 shadow-sm glass-card">
              <h6>Certified Experts</h6>
              <p className="text-muted">Experienced professionals with CISSP, CEH, and CompTIA Security+ certifications.</p>
            </Card>
          </Col>
          <Col md={6} lg={3} data-aos="fade-right" data-aos-delay="100">
            <Card className="text-center p-4 shadow-sm glass-card">
              <h6>24/7 Monitoring</h6>
              <p className="text-muted">Round-the-clock monitoring to identify threats before they escalate.</p>
            </Card>
          </Col>
          <Col md={6} lg={3} data-aos="fade-right" data-aos-delay="200">
            <Card className="text-center p-4 shadow-sm glass-card">
              <h6>Tailored Security Plans</h6>
              <p className="text-muted">Customized solutions to match your industry’s compliance and risk profile.</p>
            </Card>
          </Col>
          <Col md={6} lg={3} data-aos="fade-right" data-aos-delay="300">
            <Card className="text-center p-4 shadow-sm glass-card">
              <h6>Scalable Protection</h6>
              <p className="text-muted">Flexible security frameworks that grow with your business needs.</p>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* CTA */}
      <Container className="text-center mt-5" data-aos="zoom-in">
        <h4 className="fw-bold mb-3">Stay Ahead of Cyber Threats</h4>
        <p className="text-muted mb-4">
          Contact us today for a comprehensive security assessment and protect your business from evolving cyber risks.
        </p>
        <Button variant="primary" href="/contact" className="cta-button shadow">
          Request a Security Audit
        </Button>
      </Container>
    </div>
  );
};

export default CyberSecurityServices;
